import type { WireToggleComponent } from "@glide/fluent-components/dist/js/base-components";
import { WireToggleKind } from "@glide/fluent-components/dist/js/base-components";
import { getLocalizedString } from "@glide/localization";
import { ValueChangeSource } from "@glide/wire";
import * as React from "react";
import "twin.macro";

import { PagesCheckbox } from "../../components/pages-checkbox";
import { PagesSwitch, type PageSwitchSizes } from "../../components/pages-switch";
import { getWireValue } from "../../wire-lib";
import type { WireRenderer } from "../wire-renderer";

export const WireSwitch: WireRenderer<WireToggleComponent> = React.memo(p => {
    const { value, backend, title, description, toggleKind, size, isRequired } = p;

    const [checked, setChecked] = getWireValue(value, backend);

    const onChange = (newValue: boolean) => {
        setChecked?.(newValue, ValueChangeSource.User);
    };

    const isCheckbox = toggleKind === WireToggleKind.Checkbox;

    if (isCheckbox) {
        const descriptions = (
            <div tw="flex flex-col flex-1">
                <div tw="flex justify-between items-baseline">
                    <div tw="text-base text-text-contextual-dark font-normal">{title}</div>
                    {isRequired && (
                        <div tw="shrink-0 ml-2 text-xs text-text-contextual-pale font-normal">
                            {getLocalizedString("required")}
                        </div>
                    )}
                </div>
                <div tw="text-sm text-text-contextual-pale">{description}</div>
            </div>
        );
        return <WireSwitchCheckboxView descriptions={descriptions} checked={checked} onChange={onChange} />;
    } else {
        return (
            <WireSwitchSwitchView
                title={title}
                description={description}
                checked={checked}
                onChange={onChange}
                size={size}
                isRequired={isRequired}
            />
        );
    }
});

interface WireSwitchViewProps {
    readonly descriptions: React.ReactNode;
    readonly checked: boolean;
    readonly onChange: (newValue: boolean) => void;
    readonly size?: PageSwitchSizes;
    readonly isRequired?: boolean;
}

const WireSwitchCheckboxView: React.VFC<WireSwitchViewProps> = p => {
    const { descriptions, checked, onChange } = p;

    return (
        <label tw="flex relative">
            <PagesCheckbox checked={checked} onChange={onChange} />
            {descriptions}
        </label>
    );
};

interface WireSwitchSwitchViewProps {
    readonly title: string | null | undefined;
    readonly description: string | null | undefined;
    readonly checked: boolean;
    readonly onChange: (newValue: boolean) => void;
    readonly size?: PageSwitchSizes;
    readonly isRequired: boolean;
}

const WireSwitchSwitchView: React.VFC<WireSwitchSwitchViewProps> = p => {
    const { title, description, checked, onChange, size, isRequired } = p;

    return (
        <div>
            {isRequired && (
                <div tw="flex justify-end mb-2">
                    <div tw="shrink-0 text-xs text-text-contextual-pale font-normal">
                        {getLocalizedString("required")}
                    </div>
                </div>
            )}
            <label tw="flex relative justify-between items-center">
                <div tw="flex flex-col">
                    <div tw="text-base text-text-contextual-dark font-semibold">{title}</div>
                    <div tw="text-sm text-text-contextual-pale">{description}</div>
                </div>
                <PagesSwitch checked={checked} onChange={event => onChange(event.currentTarget.checked)} size={size} />
            </label>
        </div>
    );
};
